import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Button } from './button';
import { Input } from './input';
import { Checkbox } from './checkbox';
import { Loader2, Share2, ImageIcon, X, Info } from 'lucide-react';
import { useToast } from './use-toast';

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

/**
 * Browse-and-apply grid for styles other users have shared. Shape mirrors
 * StaticImageSlideshow so the swap between "classic" and "shared" feels
 * native. Thumbnails load only when their card scrolls into view —
 * IntersectionObserver gates the per-card preview fetch.
 */
const SharedStylesGrid = ({ onSettingsChange, children }) => {
  const { toast } = useToast();
  const [styles, setStyles] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [appliedId, setAppliedId] = useState(null);
  const sentinelRef = useRef(null);
  const LIMIT = 12;

  const fetchPage = useCallback(async (p) => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/shared_styles?page=${p}&limit=${LIMIT}`);
      if (!r.ok) throw new Error('Failed to fetch shared styles');
      const data = await r.json();
      const incoming = data.styles || [];
      setStyles((prev) => {
        const ids = new Set(prev.map((s) => s.id));
        return [...prev, ...incoming.filter((s) => !ids.has(s.id))];
      });
      setHasMore(incoming.length === LIMIT);
    } catch (_e) {
      toast({
        title: 'Could not load shared styles',
        description: 'Please try again later.',
        className: 'bg-red-300 text-black',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchPage(0); }, [fetchPage]);

  // Infinite scroll: when the bottom sentinel enters view, request next page.
  useEffect(() => {
    if (!hasMore || loading) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loading) {
        const next = page + 1;
        setPage(next);
        fetchPage(next);
      }
    }, { threshold: 0.1 });
    if (sentinelRef.current) obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [hasMore, loading, page, fetchPage]);

  const applyStyle = async (id) => {
    try {
      const r = await fetch(`${API_URL}/shared_styles/${id}`);
      if (!r.ok) throw new Error('Failed to fetch style');
      const data = await r.json();
      const settings = data?.style?.settings;
      if (settings) {
        onSettingsChange(settings);
        setAppliedId(id);
        toast({
          title: 'Style applied',
          description: data?.style?.style_name || '',
          className: 'bg-green-200 text-black',
          duration: 2000,
        });
      }
    } catch (_e) {
      toast({
        title: 'Could not apply style',
        className: 'bg-red-300 text-black',
        duration: 3000,
      });
    }
  };

  return (
    <div className="relative space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-2 lg:gap-4 w-full max-h-[calc(1.8*10rem)] md:max-h-[calc(3*10rem)] lg:max-h-[calc(4*10rem)] overflow-y-auto p-2 mb-4">
        {styles.map((s) => (
          <SharedStyleCard
            key={s.id}
            style={s}
            isSelected={selectedId === s.id}
            isApplied={appliedId === s.id}
            onSelect={() => setSelectedId(s.id)}
          />
        ))}
        <div ref={sentinelRef} className="col-span-full h-4">
          {loading && (
            <div className="flex justify-center py-2">
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            </div>
          )}
        </div>
        {!loading && styles.length === 0 && (
          <div className="col-span-full text-center text-sm text-gray-500 py-8">
            No shared styles yet — be the first to share one!
          </div>
        )}
      </div>
      <div className="flex justify-center space-x-2 items-center">
        <div className="w-4 h-4 flex items-center justify-center">
          {selectedId && (
            <div className="text-gray-700 animate-wiggle">➜</div>
          )}
        </div>
        <Button
          size="sm"
          variant="black"
          onClick={() => selectedId && applyStyle(selectedId)}
          disabled={!selectedId}
          className={`transition-all duration-300 border-0 text-xs lg:text-md ${
            selectedId ? 'rounded-3xl' : 'rounded-3xl bg-gray-100 text-gray-400'
          }`}
        >
          Select Style
        </Button>
        <div className="space-x-2 flex">{children}</div>
      </div>
    </div>
  );
};

// Per-card thumbnail. Uses IntersectionObserver so the fetch fires only when
// the card actually scrolls into view — keeps bandwidth low when the list
// gets long.
const SharedStyleCard = ({ style, isSelected, isApplied, onSelect }) => {
  const [src, setSrc] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (loaded || !ref.current) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setSrc(`${API_URL}/shared_styles/${style.id}/preview`);
        setLoaded(true);
        obs.disconnect();
      }
    }, { threshold: 0.1 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [style.id, loaded]);

  return (
    <div
      ref={ref}
      onClick={onSelect}
      className={`aspect-[4/3] relative group cursor-pointer rounded-xl
        ${isSelected ? 'ring-2 ring-blue-500' : ''}
        ${isApplied ? 'ring-2 ring-red-500' : ''}`}
    >
      {src ? (
        <img
          src={src}
          alt={style.style_name}
          className="w-full h-full object-cover rounded-lg border border-gray-200 rounded-xl"
        />
      ) : (
        <div className="w-full h-full bg-gray-100 rounded-lg flex items-center justify-center">
          <ImageIcon className="h-6 w-6 text-gray-300" />
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2 rounded-xl group-hover:bg-opacity-70 transition-colors">
        <p className="text-xs truncate font-medium">{style.style_name}</p>
        <p className="text-[10px] truncate text-gray-200">by {style.display_name}</p>
      </div>
    </div>
  );
};

/**
 * Modal opened from a map thumbnail (Your Maps / unlocked recent maps).
 * Captures style name + optional share-name, posts the style with a
 * `source: {kind, id}` pointer so the backend can copy that map's preview.
 */
const ShareStyleDialog = ({
  open,
  onOpenChange,
  settings,           // current map's settings — visual fields filtered server-side
  source,             // {kind: 'recent'|'image', id: '...'}
  defaultStyleName = '',
}) => {
  const { toast } = useToast();
  const [styleName, setStyleName] = useState(defaultStyleName);
  const [shareName, setShareName] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setStyleName(defaultStyleName);
      setShareName(false);
      setDisplayName('');
    }
  }, [open, defaultStyleName]);

  const submit = async () => {
    if (!styleName.trim()) {
      toast({
        title: 'Style name required',
        className: 'bg-amber-200 text-black',
        duration: 2500,
      });
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/shared_styles`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          style_name: styleName.trim(),
          share_name: shareName ? (displayName.trim() || null) : null,
          settings,
          source,
        }),
      });
      if (r.status === 409) {
        const data = await r.json().catch(() => ({}));
        toast({
          title: 'Share limit reached',
          description: data?.error || '',
          className: 'bg-amber-200 text-black',
          duration: 4000,
        });
        return;
      }
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data?.error || 'Share failed');
      }
      toast({
        title: 'Style shared',
        description: 'Other users can now apply your style.',
        className: 'bg-green-200 text-black',
        duration: 3000,
      });
      onOpenChange(false);
    } catch (e) {
      toast({
        title: 'Could not share style',
        description: e.message,
        className: 'bg-red-300 text-black',
        duration: 3000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    open ? (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
        onClick={() => onOpenChange(false)}
      >
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Share this style</h3>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              className="text-gray-500 hover:text-gray-700 h-8 w-8"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          <div className="px-5 py-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-800">Style name</label>
                <Input
                  value={styleName}
                  onChange={(e) => setStyleName(e.target.value)}
                  placeholder="e.g. Pastel Highlands"
                  maxLength={80}
                  autoFocus
                  className="h-11 text-base px-4 w-full"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 h-5">
                  <Checkbox
                    id="share-name"
                    checked={shareName}
                    onCheckedChange={(v) => setShareName(!!v)}
                  />
                  <label htmlFor="share-name" className="text-sm font-medium text-gray-800 select-none cursor-pointer">
                    Share my name
                  </label>
                </div>
                {shareName ? (
                  <Input
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Display name (leave empty for Anonymous)"
                    maxLength={60}
                    className="h-11 text-base px-4 w-full"
                  />
                ) : (
                  <p className="text-xs text-gray-500 h-11 flex items-center">
                    Will be listed as Anonymous.
                  </p>
                )}
              </div>
            </div>

            <div className="border-t border-gray-200" />

            <div className="flex gap-2 items-start text-xs text-gray-600 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <Info className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p>
                A low-resolution preview of this map will be shown alongside the
                style under "Styles shared by users". Personal activity routes
                drawn on it will be visible to others, though only at preview
                resolution.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={submitting}
              className="bg-gray-900 text-white hover:bg-gray-700"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Share2 className="h-4 w-4 mr-2" />}
              Share
            </Button>
          </div>
        </div>
      </div>
    ) : null
  );
};

export { SharedStylesGrid, ShareStyleDialog };
